import {isLoggedIn} from '@github-ui/client-env'
import {isApproachingRateLimit} from '@github-ui/issue-viewer/NavRateLimit'
import type {ViewNavState} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useCachePreheatIssueViewerQueries} from '@github-ui/issue-viewer/UseCachePreheatIssueViewer'
import {useLocation} from '@github-ui/react-router'
import {useCallback, useMemo, useState} from 'react'

import {useLoadMoreNavItems} from './use-load-more-nav-items'

type ViewNavRouterState = ViewNavState & {
  searchQuery?: string
}

/**
 * Manages the view navigation state for j/k keyboard navigation between issues.
 * Reads nav context from React Router state (set at list click time),
 * tracks the current item list, and handles loading additional pages.
 */
export function useViewNav() {
  const location = useLocation()
  const isLoggedInUser = isLoggedIn()

  // Extract nav state passed via React Router from the list view
  const routerNavState =
    (
      location.state as {
        viewNav?: ViewNavRouterState
      } | null
    )?.viewNav ?? null

  // Local copy of items that grows as more pages are loaded
  const [navItems, setNavItems] = useState<ItemIdentifier[]>(routerNavState?.items ?? [])
  const [backItemCount, setBackItemCount] = useState(routerNavState?.backItemCount ?? 0)
  const {loadMore, loadBack} = useLoadMoreNavItems()

  const searchQuery = routerNavState?.searchQuery ?? ''

  // Merge the router state with the current (potentially expanded) items list
  const viewNavState = useMemo(
    () => (routerNavState ? {...routerNavState, items: navItems, backItemCount} : null),
    [routerNavState, navItems, backItemCount],
  )

  useCachePreheatIssueViewerQueries({
    // Skip queueing new preheats once we're approaching the per-minute rate limit
    issues: isApproachingRateLimit(isLoggedInUser) ? [] : navItems,
    source: 'view-nav',
    target: 'full-page',
  })

  // Fetches items from the previous page and prepends them
  const onLoadBack = useCallback(async () => {
    if (backItemCount <= 0 || !searchQuery) return
    const prevItems = await loadBack(searchQuery, backItemCount)
    if (prevItems.length > 0) {
      const existingKeys = new Set(navItems.map(i => `${i.owner}/${i.repo}/${i.number}`))
      const dedupedItems = prevItems.filter(i => !existingKeys.has(`${i.owner}/${i.repo}/${i.number}`))
      if (dedupedItems.length > 0) {
        setNavItems(prev => [...dedupedItems, ...prev])
        setBackItemCount(count => count - dedupedItems.length)
      }
    }
  }, [loadBack, backItemCount, navItems, searchQuery, setNavItems, setBackItemCount])

  // Fetches the next page of issues and deduplicates against existing items
  const onLoadMore = useCallback(async () => {
    const totalCount = routerNavState?.totalCount ?? 0
    if (!searchQuery || navItems.length >= totalCount) {
      return
    }
    const result = await loadMore({currentItemCount: navItems.length, totalCount, searchQuery})
    if (result.items.length > 0) {
      const existingKeys = new Set(navItems.map(i => `${i.owner}/${i.repo}/${i.number}`))
      const dedupedItems = result.items.filter(i => !existingKeys.has(`${i.owner}/${i.repo}/${i.number}`))
      if (dedupedItems.length > 0) {
        setNavItems(prev => [...prev, ...dedupedItems])
      }
    }
  }, [loadMore, navItems, routerNavState?.totalCount, searchQuery, setNavItems])

  return {viewNavState, onLoadMore, onLoadBack}
}
